import { Component, forwardRef, inject, viewChild, ChangeDetectionStrategy } from "@angular/core";
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from "@angular/forms";
import { MatAutocompleteTrigger, } from "@angular/material/autocomplete";
import { MatChipInput } from "@angular/material/chips";
import { BehaviorSubject, combineLatest, switchMap } from "rxjs";
import { first, map, startWith } from "rxjs/operators";
import { GROUPS_ALL } from "../constants/groups.constants";
import { HierarchySelectionDialogComponent } from "../hierarchy-selection-dialog/hierarchy-selection-dialog.component";
import { AssetGroupService } from "../../../../features/asset/asset-group.service";
import { unique } from "remeda";
import { GroupItemModel } from "../group.interface";
import {DialogService} from "../../../../common/dialog/dialog.service";

type ChangeFn = (value: number[]) => unknown;

@Component({
  selector: "loren-hierarchy-autocomplete-field",
  templateUrl: "./hierarchy-autocomplete-field.component.html",
  styleUrls: ["./hierarchy-autocomplete-field.component.css"],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HierarchyAutocompleteFieldComponent),
      multi: true,
    }
  ]
})
export class HierarchyAutocompleteFieldComponent implements ControlValueAccessor {
  private readonly dialogService = inject(DialogService);
  private readonly assetGroups = inject(AssetGroupService);

  private readonly chipInputDirective = viewChild(MatChipInput);
  private readonly trigger = viewChild(MatAutocompleteTrigger);

  protected isDisabled = false;

  protected readonly query = new FormControl<string>('');
  protected readonly selectedGroupIds$ = new BehaviorSubject<number[]>([]);

  protected readonly query$ = this.query.valueChanges.pipe(
    map((query) => query || ''),
    startWith(''),
  );

  private readonly groups$ = this.assetGroups.all.pipe(
    // Remove "All Group"
    map(groups => groups.filter(x => x.name !== GROUPS_ALL)),
  );

  protected readonly selectedGroups$ = combineLatest([this.selectedGroupIds$, this.groups$]).pipe(
    map(([value, groups]) => groups.filter(x => value.includes(x.id)))
  )

  autocompleteItems$ = combineLatest([this.query$, this.groups$]).pipe(
    map(([query, groups]) => {
      const filterValue = query.toLowerCase();

      if (filterValue === '') {
        return groups;
      }

      return groups.filter(x => x.name.toLowerCase().includes(filterValue));
    })
  );

  onChange: ChangeFn;
  onTouched: () => unknown;

  writeValue(newValue: number[]): void {
    this.selectedGroupIds$.next(newValue);
  }

  registerOnChange(fn: ChangeFn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => unknown): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  remove(groupId: number) {
    this.updateValue(this.selectedGroupIds$.value.filter(x => x !== groupId))
  }

  handleSelect(group: GroupItemModel): void {
    this.updateValue(unique([
      ...this.selectedGroupIds$.value,
      group.id
    ]));
    this.chipInputDirective().clear();
    this.query.setValue('');
  }

  openHierarchySelectionDialog() {
    this.trigger().closePanel();
    this.trigger().autocompleteDisabled = true;
    this.selectedGroups$.pipe(
      first(),
      switchMap(selectedGroups => {
        const dialogRef = this.dialogService.open(HierarchySelectionDialogComponent, {
          width: "550px",
          height: "600px",
          data: {
            showAssets: false,
            showCheckboxes: true,
            selectedGroups,
          },
        })

        return dialogRef.afterClosed();
      }),
    ).subscribe((result) => {
      this.trigger().autocompleteDisabled = false;
      if (!result) {
        return;
      }

      this.updateValue(result.selectedGroups.map((x: GroupItemModel) => x.id));
    });
  }

  private updateValue(newValue: number[]) {
    this.selectedGroupIds$.next(newValue);
    this.onChange(newValue);
    this.onTouched();
  }

}
